import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Briefcase, FileText, Users, BarChart3, Search, ArrowRight, Star, Sparkles, TrendingUp, CheckCircle2, LogOut, Building2, CreditCard, User, Shield, Video, HelpCircle, Monitor, Globe, Gift, Bookmark, Wrench, Calendar, BookOpen, DollarSign, Moon, Sun, PlusCircle, Eye, ClipboardList, UserCheck } from 'lucide-react';
import OnboardingTour from '../components/OnboardingTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RoleBadge({ role }) {
  if (role === 'business') return <span className="tag-pill text-[10px] bg-[#D97757]/15 text-[#D97757] font-medium">Employer</span>;
  if (role === 'admin') return <span className="tag-pill text-[10px] bg-red-100 text-red-600 font-medium">Admin</span>;
  return <span className="tag-pill text-[10px] bg-[#047857]/10 text-[#047857] font-medium">Job Seeker</span>;
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { dark, toggleDark } = useTheme();
  const [quote, setQuote] = useState(null);
  const [stats, setStats] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [businessStats, setBusinessStats] = useState(null);

  const isBusiness = user?.role === 'business';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const seen = localStorage.getItem('wfj-tour-done');
    if (!seen) setShowTour(true);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [quoteRes, statsRes] = await Promise.all([
          axios.get(`${API}/quotes/daily`),
          axios.get(`${API}/dashboard/stats`, { withCredentials: true })
        ]);
        setQuote(quoteRes.data.quote);
        setStats(statsRes.data);
      } catch (err) { console.error(err); }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isBusiness) {
      axios.get(`${API}/business/my-company`, { withCredentials: true })
        .then(r => setBusinessStats({ company: r.data.company, jobs: r.data.jobs || [] }))
        .catch(() => {});
    }
  }, [isBusiness]);

  // ─── EMPLOYER DASHBOARD ───
  if (isBusiness) {
    const hasCompany = businessStats?.company;
    const postedJobs = businessStats?.jobs || [];
    const hasPlan = ['starter', 'growth', 'enterprise'].includes(user?.membership);

    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
              <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/business" className="text-sm text-[#1C2A24]/70 hover:text-[#D97757] transition-colors">Business Portal</Link>
              <Link to="/jobs" className="text-sm text-[#1C2A24]/70 hover:text-[#047857] transition-colors">Browse Jobs</Link>
              <Link to="/hiring-events" className="text-sm text-[#1C2A24]/70 hover:text-[#047857] transition-colors">Hiring Events</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-[#1C2A24]/60 hidden md:inline">{user?.name}</span>
              <RoleBadge role={user?.role} />
              <span className="tag-pill text-xs">{user?.membership || 'free'}</span>
              <button onClick={toggleDark} className="w-8 h-8 rounded-full bg-[#F4F1E8] flex items-center justify-center hover:bg-[#E5E0D8] transition-colors" data-testid="dark-mode-toggle">
                {dark ? <Sun size={14} className="text-[#D97757]" strokeWidth={1.5} /> : <Moon size={14} className="text-[#1C2A24]/60" strokeWidth={1.5} />}
              </button>
              <Button variant="ghost" size="sm" onClick={logout} className="rounded-full" data-testid="logout-btn"><LogOut size={16} strokeWidth={1.5} /></Button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          {/* Employer Header */}
          <div className="flex items-center gap-4 mb-8" data-testid="employer-dashboard-header">
            <div className="w-14 h-14 rounded-2xl bg-[#D97757] flex items-center justify-center">
              <Building2 size={24} className="text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-medium tracking-tight" style={{fontFamily:'Outfit'}}>
                {hasCompany ? hasCompany.company_name : 'Employer Dashboard'}
              </h1>
              <p className="text-[#1C2A24]/60 text-sm mt-0.5">
                {hasCompany ? 'Manage your job listings and applicants' : 'Set up your company and start hiring'}
              </p>
            </div>
          </div>

          {/* Employer Stats */}
          {hasPlan && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="employer-stats">
              <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <p className="text-sm text-[#1C2A24]/60 mb-1">Posted Jobs</p>
                <p className="text-2xl font-semibold text-[#D97757]" style={{fontFamily:'Outfit'}}>{postedJobs.length}</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <p className="text-sm text-[#1C2A24]/60 mb-1">Total Applicants</p>
                <p className="text-2xl font-semibold text-[#047857]" style={{fontFamily:'Outfit'}}>{postedJobs.reduce((s, j) => s + (j.applicants || 0), 0)}</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <p className="text-sm text-[#1C2A24]/60 mb-1">Company</p>
                <p className="text-lg font-semibold" style={{fontFamily:'Outfit'}}>{hasCompany ? <CheckCircle2 size={22} className="text-[#047857]" /> : <span className="text-[#D97757] text-sm">Not set up</span>}</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <p className="text-sm text-[#1C2A24]/60 mb-1">Plan</p>
                <p className="text-lg font-semibold text-[#047857] capitalize" style={{fontFamily:'Outfit'}}>{user?.membership}</p>
              </div>
            </div>
          )}

          {/* Primary Employer Actions */}
          <h2 className="text-xl font-medium mb-4" style={{fontFamily:'Outfit'}}>Employer Tools</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="employer-actions">
            <Link to="/business" className="bg-white rounded-3xl border border-[#D97757]/20 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(217,119,87,0.1)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#D97757] flex items-center justify-center mb-4"><PlusCircle size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>Post a Job</h3>
              <p className="text-sm text-[#1C2A24]/50">Create a new WFH job listing</p>
              <ArrowRight size={16} className="text-[#D97757] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
            <Link to="/business" className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#047857] flex items-center justify-center mb-4"><ClipboardList size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>Manage Listings</h3>
              <p className="text-sm text-[#1C2A24]/50">{postedJobs.length} active job {postedJobs.length === 1 ? 'listing' : 'listings'}</p>
              <ArrowRight size={16} className="text-[#047857] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
            <Link to="/business" className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#86A789] flex items-center justify-center mb-4"><UserCheck size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>View Applicants</h3>
              <p className="text-sm text-[#1C2A24]/50">Review candidate applications</p>
              <ArrowRight size={16} className="text-[#86A789] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
            <Link to="/hiring-events" className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#D97757] flex items-center justify-center mb-4"><Video size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>Hiring Events</h3>
              <p className="text-sm text-[#1C2A24]/50">Host live interviews</p>
              <ArrowRight size={16} className="text-[#D97757] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
            <Link to="/membership" className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#047857] flex items-center justify-center mb-4"><CreditCard size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>Manage Plan</h3>
              <p className="text-sm text-[#1C2A24]/50">Upgrade or manage subscription</p>
              <ArrowRight size={16} className="text-[#047857] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
            <Link to="/profile" className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className="w-11 h-11 rounded-2xl bg-[#86A789] flex items-center justify-center mb-4"><User size={20} className="text-white" strokeWidth={1.5} /></div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>Account Settings</h3>
              <p className="text-sm text-[#1C2A24]/50">Update profile & preferences</p>
              <ArrowRight size={16} className="text-[#86A789] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
          </div>

          {/* No plan yet — upgrade prompt */}
          {!hasPlan && (
            <div className="bg-gradient-to-r from-[#D97757]/10 to-[#D97757]/5 rounded-3xl border border-[#D97757]/20 p-8 mb-8" data-testid="employer-upgrade-prompt">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#D97757] flex items-center justify-center shrink-0">
                  <TrendingUp size={22} className="text-white" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-medium mb-1" style={{fontFamily:'Outfit'}}>Activate Your Business Account</h3>
                  <p className="text-sm text-[#1C2A24]/60 mb-4">Subscribe to a business plan to start posting jobs, hosting hiring events, and reviewing applicants.</p>
                  <div className="flex flex-wrap gap-4 text-xs text-[#1C2A24]/50 mb-4">
                    <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-[#047857]" /> Post remote job listings</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-[#047857]" /> Host live hiring events</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-[#047857]" /> Review applicants & resumes</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={12} className="text-[#047857]" /> Featured employer badge</span>
                  </div>
                  <Link to="/membership">
                    <Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white h-11 px-6" data-testid="upgrade-to-business-btn">
                      View Business Plans — Starting at $99/mo <ArrowRight size={14} className="ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Secondary: Job Seeker Tools */}
          <div className="border-t border-[#E5E0D8] pt-8 mt-4">
            <h2 className="text-base text-[#1C2A24]/50 mb-4" style={{fontFamily:'Outfit'}}>Also available</h2>
            <div className="grid md:grid-cols-4 gap-3" data-testid="secondary-tools">
              {[
                { to: '/jobs', icon: Search, label: 'Browse Jobs' },
                { to: '/community', icon: Users, label: 'Community' },
                { to: '/blog', icon: BookOpen, label: 'Blog' },
                { to: '/support', icon: HelpCircle, label: 'Support' },
              ].map((item, i) => (
                <Link to={item.to} key={i} className="flex items-center gap-3 bg-white rounded-xl border border-[#E5E0D8] p-3 hover:border-[#047857]/30 transition-all">
                  <item.icon size={16} className="text-[#1C2A24]/40" strokeWidth={1.5} />
                  <span className="text-sm text-[#1C2A24]/60">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
          {isAdmin && (
            <div className="mt-6"><Link to="/admin"><Button variant="outline" className="rounded-full border-red-200 text-red-600 text-sm"><Shield size={14} className="mr-2" /> Admin Dashboard</Button></Link></div>
          )}
        </div>
      </div>
    );
  }

  // ─── JOB SEEKER DASHBOARD (default) ───
  const NAV_ITEMS = [
    { to: '/jobs', icon: Search, label: 'Find Jobs', desc: 'Browse WFH opportunities', color: 'bg-[#047857]' },
    { to: '/resume-builder', icon: FileText, label: 'Resume Builder', desc: 'AI-powered resume creation', color: 'bg-[#D97757]' },
    { to: '/applications', icon: BarChart3, label: 'My Applications', desc: 'Track your progress', color: 'bg-[#86A789]' },
    { to: '/saved-jobs', icon: Bookmark, label: 'Saved Jobs', desc: 'Your bookmarked jobs', color: 'bg-[#D97757]' },
    { to: '/tools', icon: Wrench, label: 'WFH Tools', desc: 'Typing test, speed test', color: 'bg-[#047857]' },
    { to: '/hiring-events', icon: Video, label: 'Hiring Events', desc: 'Live interviews & hiring', color: 'bg-[#86A789]' },
    { to: '/community', icon: Users, label: 'Community', desc: 'Connect & network', color: 'bg-[#86A789]' },
    { to: '/webinars', icon: Calendar, label: 'Webinars', desc: 'Live sessions & workshops', color: 'bg-[#D97757]' },
    { to: '/help-hub', icon: BookOpen, label: 'WFH Help Hub', desc: 'Setup guides & onboarding', color: 'bg-[#047857]' },
    { to: '/blog', icon: BookOpen, label: 'WFH Blog', desc: 'Tips, guides & articles', color: 'bg-[#D97757]' },
    { to: '/salary-calculator', icon: DollarSign, label: 'Salary Calculator', desc: 'Compare WFH salaries', color: 'bg-[#86A789]' },
    { to: '/equipment', icon: Monitor, label: 'WFH Equipment', desc: 'Recommended gear', color: 'bg-[#86A789]' },
    { to: '/platforms', icon: Globe, label: 'WFH Platforms', desc: 'Arise, Omni & more', color: 'bg-[#047857]' },
    { to: '/membership', icon: CreditCard, label: 'Membership', desc: 'Manage your plan', color: 'bg-[#047857]' },
    { to: '/support', icon: HelpCircle, label: 'Support', desc: 'Get help & support', color: 'bg-[#86A789]' },
    { to: '/referrals', icon: Gift, label: 'Refer a Friend', desc: 'Earn free Plus months', color: 'bg-[#D97757]' },
    { to: '/profile', icon: User, label: 'My Profile', desc: 'Update your info', color: 'bg-[#047857]' },
  ];

  if (isAdmin) {
    NAV_ITEMS.push({ to: '/admin', icon: Shield, label: 'Admin Dashboard', desc: 'Manage platform', color: 'bg-red-500' });
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-sm text-[#1C2A24]/70 hover:text-[#047857] transition-colors">Jobs</Link>
            <Link to="/community" className="text-sm text-[#1C2A24]/70 hover:text-[#047857] transition-colors">Community</Link>
            <Link to="/profile" className="text-sm text-[#1C2A24]/70 hover:text-[#047857] transition-colors">Profile</Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#1C2A24]/60 hidden md:inline">Hi, {user?.name || 'there'}</span>
            <RoleBadge role={user?.role} />
            <span className="tag-pill text-xs">{user?.membership || 'free'}</span>
            <button onClick={toggleDark} className="w-8 h-8 rounded-full bg-[#F4F1E8] flex items-center justify-center hover:bg-[#E5E0D8] transition-colors" data-testid="dark-mode-toggle">
              {dark ? <Sun size={14} className="text-[#D97757]" strokeWidth={1.5} /> : <Moon size={14} className="text-[#1C2A24]/60" strokeWidth={1.5} />}
            </button>
            <Button variant="ghost" size="sm" onClick={logout} className="rounded-full" data-testid="logout-btn"><LogOut size={16} strokeWidth={1.5} /></Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {quote && (
          <div className="quote-card rounded-3xl p-8 mb-8 border border-[#E5E0D8] animate-fade-up" data-testid="dashboard-quote">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#047857]/10 flex items-center justify-center shrink-0 animate-float">
                <Sparkles size={18} className="text-[#047857]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-lg md:text-xl font-light leading-relaxed text-[#1C2A24]" style={{fontFamily:'Outfit'}}>"{quote.quote}"</p>
                <p className="text-sm text-[#047857] mt-3 font-medium">- {quote.author}</p>
              </div>
            </div>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 animate-fade-up stagger-1" data-testid="dashboard-stats">
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <p className="text-sm text-[#1C2A24]/60 mb-1">Total Jobs</p>
              <p className="text-2xl font-semibold text-[#047857]" style={{fontFamily:'Outfit'}}>{stats.total_jobs}</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <p className="text-sm text-[#1C2A24]/60 mb-1">Applied</p>
              <p className="text-2xl font-semibold text-[#D97757]" style={{fontFamily:'Outfit'}}>{stats.total_applications}</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <p className="text-sm text-[#1C2A24]/60 mb-1">Active</p>
              <p className="text-2xl font-semibold text-[#86A789]" style={{fontFamily:'Outfit'}}>{stats.active_applications}</p>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <p className="text-sm text-[#1C2A24]/60 mb-1">Profile</p>
              <p className="text-2xl font-semibold" style={{fontFamily:'Outfit'}}>
                {stats.profile_complete ? <CheckCircle2 size={24} className="text-[#047857]" /> : <span className="text-[#D97757]">Incomplete</span>}
              </p>
            </div>
          </div>
        )}

        <h2 className="text-xl font-medium mb-4" style={{fontFamily:'Outfit'}}>Quick Actions</h2>
        <div className="grid md:grid-cols-3 gap-4 animate-fade-up stagger-2" data-testid="quick-actions">
          {NAV_ITEMS.map((item, i) => (
            <Link to={item.to} key={i} className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(4,120,87,0.08)] transition-all duration-300 group">
              <div className={`w-11 h-11 rounded-2xl ${item.color} flex items-center justify-center mb-4`}>
                <item.icon size={20} className="text-white" strokeWidth={1.5} />
              </div>
              <h3 className="text-base font-medium mb-1" style={{fontFamily:'Outfit'}}>{item.label}</h3>
              <p className="text-sm text-[#1C2A24]/50">{item.desc}</p>
              <ArrowRight size={16} className="text-[#047857] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
            </Link>
          ))}
        </div>

        {stats && !stats.profile_complete && (
          <div className="mt-8 bg-[#D97757]/10 rounded-2xl p-6 border border-[#D97757]/20 animate-fade-up stagger-3" data-testid="profile-reminder">
            <div className="flex items-center gap-3">
              <Star size={20} className="text-[#D97757]" strokeWidth={1.5} />
              <div>
                <p className="font-medium text-[#1C2A24]" style={{fontFamily:'Outfit'}}>Complete Your Profile for Quick Apply</p>
                <p className="text-sm text-[#1C2A24]/60 mt-1">Add your name, phone, location, and skills to enable one-click applications.</p>
              </div>
              <Link to="/profile" className="ml-auto">
                <Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white" data-testid="complete-profile-btn">Complete Profile</Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {showTour && (
        <OnboardingTour onComplete={() => {
          setShowTour(false);
          localStorage.setItem('wfj-tour-done', 'true');
        }} />
      )}
    </div>
  );
}
